/**
 * 약관 서비스 (Firestore `terms/{type}` 문서 기반)
 * - seedTerms.js 로 초기 데이터 업로드 완료 (2026-04-23)
 * - DEFAULT_TERMS 는 네트워크 장애 시 폴백 용도로만 유지
 */
import { auth, db } from '../firebase/config';
import {
  doc, getDoc, getDocs, collection, setDoc, updateDoc, serverTimestamp,
  addDoc, query, where, orderBy, limit as fsLimit,
} from 'firebase/firestore';

const COL = 'terms';
const VERSIONS_COL = 'terms_versions';

export const TERMS_TYPES = [
  { key: 'service', label: '서비스 이용약관', editable: true },
  { key: 'privacy', label: '개인정보 처리방침', editable: true },
  { key: 'partner_contract', label: '제휴업체 이용약관 및 계약', editable: true },
  { key: 'purchase_refund', label: '구매 및 환불 동의', editable: true },
  { key: 'operation_policy', label: '운영정책', editable: true },
  { key: 'third_party', label: '개인정보 제3자 제공동의', editable: true },
  { key: 'opensource', label: '오픈소스 라이선스', editable: false },
  { key: 'realtor_consent', label: '공인중개사 동의서', editable: true },
];

const EDITABLE_KEYS = TERMS_TYPES.filter((t) => t.editable).map((t) => t.key);

// 오픈소스는 고정값
const OPENSOURCE_FIXED = {
  type: 'opensource',
  title: '오픈소스 라이선스',
  version: '1.0.0',
  effective_date: '2026-01-01',
  content: `본 앱은 아래와 같은 오픈소스 라이선스를 따릅니다.

React
MIT License. UI 라이브러리.

React Router
MIT License. SPA 라우팅.

styled-components
MIT License. CSS-in-JS.

react-icons
MIT License. 아이콘 세트.

recharts
MIT License. 차트 라이브러리.

Firebase
Apache License 2.0. 백엔드·인증·DB.

기타 사용 라이브러리의 상세 라이선스는 각 패키지 문서를 참고해 주세요.
문의: ieum.desk@gmail.com`,
  updated_at: '2026-04-01T00:00:00.000Z',
};

const DEFAULT_TERMS = {
  service: {
    type: 'service',
    title: '서비스 이용약관',
    version: '1.0.0',
    effective_date: '2026-04-01',
    content: `이음 서비스 이용약관

제1조 (목적)
본 약관은 이음(이하 "회사")이 제공하는 서비스의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.

제2조 (정의)
"이용자"란 본 약관에 따라 서비스를 이용하는 고객, 공인중개사, 제휴업체를 의미합니다.
"DB"란 고객이 입력한 서비스 요청 정보를 의미합니다.
"제휴업체"란 회사와 별도의 계약을 체결하고 서비스를 제공하는 사업자를 의미합니다.

제3조 (서비스 내용)
회사는 이용자가 입력한 정보를 기반으로 관련 서비스에 대한 정보 제공 및 이용 기회를 제공하는 플렛폼입니다.

제4조 (서비스 제공 및 변경)
회사는 서비스의 일부 또는 전부를 변경하거나 중단할 수 있습니다.
서비스 변경 시 사전에 공지합니다.

제5조 (회원의 의무)
이용자는 다음 사항을 준수하여야 합니다.
관련 법령 및 본 약관을 준수할 것
서비스 이용 과정에서 허위 정보 제공 금지
타인의 권리를 침해하지 않을 것

제6조 (서비스 이용 제한)
회사는 다음 경우 서비스 이용을 제한할 수 있습니다.
약관 위반
부정 이용
서비스 운영 방해

제7조 (플렛폼의 지위)
회사는 정보 제공 플렛폼으로서 직접적인 서비스 제공자가 아니며, 고객과 제휴업체 간 계약, 상담 및 거래에 관여하지 않습니다.

제8조 (책임의 한계)
회사는 고의 또는 중대한 과실이 없는 한 서비스 이용과 관련하여 책임을 지지 않습니다.
회사는 제휴업체가 제공하는 서비스의 내용, 품질, 결과 등에 대해 책임을 지지 않습니다.

제9조 (데이터에 대한 책임)
회사는 제공되는 정보의 정확성에 대해 보증하지 않으며, 이용자는 이를 참고하여 상담 및 계약을 진행합니다.

제10조 (약관 변경)
회사는 관련 법령을 위반하지 않는 범위에서 본 약관을 변경할 수 있으며, 변경 시 사전에 공지합니다.

제11조 (분쟁 해결)
서비스 이용과 관련하여 발생한 분쟁은 관련 법령에 따라 해결합니다.

제12조 (준거법 및 관할)
본 서비스와 관련된 분쟁은 대한민국 법을 준거법으로 하며, 관할 법원은 회사 본점 소재지 관할 법원으로 합니다.

부칙
본 약관은 2026년 4월 1일부터 시행됩니다.`,
    updated_at: '2026-04-01T00:00:00.000Z',
  },
  privacy: {
    type: 'privacy',
    title: '개인정보처리방침',
    version: '1.0.0',
    effective_date: '2026-04-01',
    content: `이음 개인정보처리방침
이음(이하 "회사")은 「개인정보 보호법」 등 관련 법령을 준수하며, 이용자의 개인정보를 보호하기 위하여 다음과 같은 처리방침을 수립·공개합니다.

제1조 (수집하는 개인정보 항목)
회사는 서비스 제공을 위해 다음과 같은 개인정보를 수집합니다.
1. 필수 수집 항목
성명
연락처(휴대전화번호)
주소
서비스 요청 정보 (이사일, 평수, 지역 등)
2. 자동 수집 항목
IP 주소
접속 로그
서비스 이용 기록
쿠키 정보

제2조 (개인정보 수집 및 이용 목적)
회사는 다음 목적을 위해 개인정보를 이용합니다.
이용자가 요청한 서비스 상담 및 정보 제공
서비스 이용 관련 안내 및 고객 문의 대응
서비스 이용 이력 관리 및 운영
부정 이용 방지 및 서비스 개선

제3조 (개인정보의 제3자 제공)
회사는 이용자의 사전 동의를 받은 경우에 한하여 개인정보를 제3자에게 제공합니다.
1. 제공받는 자
이음 제휴업체 (이사업체, 청소업체, 인테리어 업체 등)
2. 제공 항목
성명, 연락처
주소 및 서비스 요청 정보
3. 제공 목적
이용자가 요청한 서비스 관련 상담 및 안내
4. 보유 및 이용기간
제공 목적 달성 시까지

제4조 (개인정보 처리의 위탁)
회사는 원활한 서비스 제공을 위해 다음과 같이 개인정보 처리를 위탁할 수 있습니다.
문자 발송 서비스 제공업체: SMS 및 알림톡 발송
클라우드 서비스 제공업체: 서버 운영 및 데이터 저장
회사는 위탁 계약 시 개인정보 보호 관련 법령을 준수하도록 관리·감독합니다.

제5조 (개인정보 보유 및 이용기간)
회사는 개인정보를 수집 및 이용 목적이 달성된 후 지체 없이 파기합니다.
단, 다음의 경우 일정 기간 보관할 수 있습니다.
서비스 이용 종료 후 6개월
관련 법령에 따라 보존이 필요한 경우 해당 기간

제6조 (개인정보의 파기 절차 및 방법)
회사는 개인정보 보유기간 경과 또는 처리 목적 달성 시 다음과 같은 방법으로 파기합니다.
파기 절차
목적 달성 후 별도 DB로 분리 저장 후 일정 기간 보관 후 파기
파기 방법
전자적 파일: 복구 불가능한 방식으로 영구 삭제
종이 문서: 분쇄 또는 소각

제7조 (이용자의 권리 및 행사 방법)
이용자는 언제든지 다음 권리를 행사할 수 있습니다.
개인정보 열람
개인정보 정정
개인정보 삭제
개인정보 처리 정지 요청
권리 행사는 이메일을 통해 요청할 수 있으며, 회사는 지체 없이 조치합니다.

제8조 (개인정보 보호 책임자)
개인정보 보호 책임자: 이음 운영팀
이메일: biz@ieum.in

제9조 (개인정보 보호 조치)
회사는 개인정보 보호를 위해 다음과 같은 조치를 취합니다.
개인정보 접근 권한 최소화
내부 관리계획 수립 및 시행
보안 프로그램 운영 및 점검

제10조 (정책 변경)
본 개인정보처리방침은 관련 법령 및 회사 정책에 따라 변경될 수 있으며, 변경 시 사전에 공지합니다.

부칙
본 방침은 2026년 4월 1일부터 시행됩니다.`,
    updated_at: '2026-04-01T00:00:00.000Z',
  },
  third_party: {
    type: 'third_party',
    title: '개인정보 제3자 제공 동의',
    version: '1.0.0',
    effective_date: '2026-04-01',
    content: `회사는 이용자가 요청한 서비스 상담을 위해 개인정보를 제3자에게 제공합니다.

1. 제공받는 자
이음 제휴업체 (이사업체, 청소업체, 인테리어 업체 등)

2. 제공 항목
성명, 연락처, 주소 및 서비스 요청 정보

3. 제공 목적
이용자가 요청한 서비스 관련 상담 및 안내

4. 보유 및 이용기간
제공 목적 달성 시까지

※ 귀하는 개인정보 제3자 제공에 대한 동의를 거부할 권리가 있으나, 동의 거부 시 서비스 이용이 제한됩니다.`,
    updated_at: '2026-04-01T00:00:00.000Z',
  },
  partner_contract: {
    type: 'partner_contract',
    title: '이음 제휴업체 이용약관 및 계약',
    version: '1.0.0',
    effective_date: '2026-04-01',
    content: `본인은 귀사("회사")의 제휴업체 가입에 있어 다음 사항에 동의하며, 본 등록 완료 직후 회사로부터 서비스 방법과 동일한 효력이 발생함을 확인합니다.

[필수] 서비스 이용 동의
본인은 회사가 제공하는 고객 요청 기반 정보 제공(DB) 서비스를 이용하여 상담 및 영업을 진행할 수 있습니다.
제공되는 고객 정보는 상담 목적에 한하여 사용하며, 상담 법령을 준수합니다.

[필수] 이용자의 의무
본인은 다음 사항을 준수합니다.
- 고객 정보를 상담 목적 외로 사용하지 않습니다.
- 회사가 제공한 데이터를 정당한 절차 없이 재판매하지 않습니다.
- 고객에게 성실하고 정확한 정보를 제공합니다.
- 고객의 민원사항이 접수되면 즉시 대응합니다.

[필수] 금지행위
다음 행위는 금지됩니다.
- 고객 정보 무단 유출 및 제3자 제공
- 고객 정보 유료 거래 및 상담 이력의 임의 수정
- 고객에게 허위 또는 과장된 정보 제공
- 회사에게 피해를 끼치는 일체의 영업 행위

[필수] DB 이용 및 환불 정책 동의
구매·배정 DB는 디지털 정보의 특성상 단순 변심에 의한 환불이 제한됩니다.
단, 다음의 경우 회사 판단에 따라 환불 또는 재발급될 수 있습니다.
- DB의 정보가 허위이거나 중복된 경우
- 시스템 오류로 잘못된 DB가 전달된 경우
- 회사의 귀책사유로 서비스를 제공할 수 없는 경우

부칙
본 약관은 2026년 4월 1일부터 시행됩니다.`,
    updated_at: '2026-04-01T00:00:00.000Z',
  },
  purchase_refund: {
    type: 'purchase_refund',
    title: '구매 및 환불 동의',
    version: '1.0.0',
    effective_date: '2026-04-01',
    content: `본인은 이음("회사")의 DB 구매 및 환불 정책에 동의합니다.

[필수] DB 구매 동의
제휴업체는 DB 마켓에서 원하는 DB를 직접 선택하여 구매할 수 있으며, 구매 즉시 고객 정보가 제공됩니다.
구매한 DB는 디지털 정보의 특성상 단순 변심에 의한 환불이 제한됩니다.

[필수] 환불 가능 사유
다음의 경우에 한해 환불이 가능합니다.
- DB 정보가 명백히 허위이거나 중복된 경우
- 시스템 오류로 잘못된 DB가 전송된 경우
- 회사의 귀책사유로 서비스가 제공되지 못한 경우

[필수] 환불 절차
환불을 원하는 제휴업체는 구매일로부터 7일 이내에 회사에 서면(이메일 포함)으로 사유를 명시하여 신청해야 합니다.
회사는 신청 접수 후 영업일 기준 5일 이내에 검토 후 환불 여부를 통보합니다.

[필수] 환불 방법 및 제한
환불이 승인된 경우 결제 수단으로 환불하거나 마일리지로 적립합니다.
이미 고객과 상담이 진행되었거나 서비스가 일부 제공된 DB는 환불이 제한됩니다.

부칙
본 정책은 2026년 4월 1일부터 시행됩니다.`,
    updated_at: '2026-04-01T00:00:00.000Z',
  },
  operation_policy: {
    type: 'operation_policy',
    title: '이음 서비스 운영정책',
    version: '1.0.0',
    effective_date: '2026-04-01',
    content: `본 정책은 이음(이하 "회사") 서비스 운영 기준 및 제휴업체의 서비스 이용 시 준수해야 할 사항을 규정합니다.

제1조 (목적)
본 정책은 이음의 서비스 운영 기준 및 제휴업체의 서비스 이용 시 준수해야 할 사항을 규정함을 목적으로 합니다.

제2조 (적용 대상)
본 정책은 이음 서비스를 이용하는 제휴업체에 적용됩니다.

제3조 (벌점 제도)
회사는 다음과 같은 벌점 제도를 운영합니다.

1. 벌점 부여 사유
- 고객 면담 약속 미이행: 1점
- 불성실 상담 신고: 1점
- 허위 정보 제공: 3점
- 개인정보 유출 시도: 5점
- 이용약관 위반: 3점
- 법령 위반 행위: 5점
- 회사의 정당한 요구 미이행: 3점

2. 벌점 누적에 따른 조치
- 3점 이상: 경고
- 5점 이상: 서비스 일부 정지
- 10점 이상: 서비스 이용 정지
- 15점 이상: 계약 해지

제4조 (페널티)
회사는 위 벌점 외에도 서비스 이용 제한 또는 보증금 회수 등의 페널티를 부과할 수 있습니다.
1. DB 활용 부정 사례 시 상당하는 비용을 면책할 수 있음
2. 고객으로부터 직접적인 피해 사례가 접수된 경우 즉시 정지
3. 회사 정책 위반 시 단계적 페널티 적용

제5조 (서비스 이용 제한)
회사는 본 정책 위반에 대해 서비스 이용 제한 조치를 취할 수 있습니다.

부칙
본 정책은 2026년 4월 1일부터 시행됩니다.`,
    updated_at: '2026-04-01T00:00:00.000Z',
  },
  opensource: OPENSOURCE_FIXED,
  realtor_consent: {
    type: 'realtor_consent',
    title: '공인중개사 동의서',
    version: '1.0.0',
    effective_date: '2026-04-01',
    content: `이음 공인중개사 서비스 이용 동의

[필수] 서비스 이용약관 동의
본인은 이음(이하 "회사")이 제공하는 정보 제공 및 플렛폼 서비스를 이용함에 있어 관련 약관 및 운영 정책에 동의합니다.

[필수] 개인정보 수집 및 이용 동의
회사는 서비스 제공을 위해 다음과 같은 정보를 수집 및 이용합니다.
1. 수집 항목
성명, 연락처
부동산명 및 소속 정보
2. 수집 목적
회원 식별 및 관리
서비스 이용 지원 및 안내
정산 및 지급 처리
3. 보유 기간
서비스 이용 기간 및 종료 후 6개월
※ 동의를 거부할 수 있으나, 거부 시 서비스 이용이 제한됩니다.

[필수] 서비스 이용 및 역할 안내 동의
본인은 다음 사항을 이해하고 동의합니다.
회사는 고객 요청 기반으로 제휴업체 정보를 제공하는 플렛폼입니다.
고객은 전용 접수 링크를 통해 직접 정보를 입력합니다.
입력된 정보는 고객 요청에 따라 제휴업체에 전달되어 상담이 진행됩니다.
본인은 해당 상담 및 계약 과정에 직접 관여하지 않습니다.
제휴업체와 고객 간 계약은 당사자 간 책임 하에 이루어집니다.

[필수] 정산 및 지급 안내 동의
본인은 회사가 제공하는 플렛폼 이용에 따른 리워드 또는 마케팅 협력 보상 체계에 동의합니다.
지급 기준 및 방식은 회사 정책에 따라 운영됩니다.
지급은 익월 정산을 원칙으로 하며, 세금 처리는 관련 법령에 따릅니다.

[필수] 유의사항
본인은 고객에게 서비스 이용을 위한 접수 링크를 안내할 수 있습니다.
허위·과장 안내 또는 부정한 방법으로 이용을 유도할 경우 서비스 이용이 제한될 수 있습니다.
본 서비스는 공인중개 업무와는 별도의 정보 제공 서비스입니다.

[필수] 책임 및 면책
회사는 제휴업체의 서비스 내용, 계약, 분쟁 등에 직접 관여하지 않습니다.
고객과 제휴업체 간 발생하는 문제는 당사자 간 해결을 원칙으로 합니다.
단, 회사는 플렛폼 운영자로서 필요한 범위 내에서 지원할 수 있습니다.

동의 확인
위 내용을 모두 확인하였으며 이에 동의합니다.`,
    updated_at: '2026-04-01T00:00:00.000Z',
  },
};

// ────── 조회 ──────

const serializeTs = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate().toISOString();
  return v;
};

export const getAllTerms = async () => {
  try {
    const snap = await getDocs(collection(db, COL));
    const result = {};
    snap.docs.forEach((d) => {
      const data = d.data();
      result[d.id] = { ...data, updated_at: serializeTs(data.updated_at) || data.updated_at };
    });
    // 오픈소스는 항상 고정값으로 덮어쓰기
    result.opensource = OPENSOURCE_FIXED;
    // Firestore 에 없는 타입은 DEFAULT_TERMS 폴백
    Object.keys(DEFAULT_TERMS).forEach((k) => {
      if (!result[k]) result[k] = DEFAULT_TERMS[k];
    });
    return result;
  } catch (err) {
    console.warn('terms 조회 실패, DEFAULT_TERMS 폴백 사용:', err);
    return { ...DEFAULT_TERMS, opensource: OPENSOURCE_FIXED };
  }
};

export const getTerm = async (type) => {
  if (type === 'opensource') return OPENSOURCE_FIXED;
  try {
    const snap = await getDoc(doc(db, COL, type));
    if (snap.exists()) {
      const data = snap.data();
      return { ...data, updated_at: serializeTs(data.updated_at) || data.updated_at };
    }
    return DEFAULT_TERMS[type] || null;
  } catch (err) {
    console.warn(`terms/${type} 조회 실패, DEFAULT_TERMS 폴백:`, err);
    return DEFAULT_TERMS[type] || null;
  }
};

// ────── 수정 (관리자) ──────

export const updateTerm = async (type, patch) => {
  if (type === 'opensource') {
    throw new Error('오픈소스 라이선스는 수정할 수 없습니다.');
  }
  if (!EDITABLE_KEYS.includes(type)) {
    throw new Error(`알 수 없는 약관 타입: ${type}`);
  }
  const ref = doc(db, COL, type);
  const snap = await getDoc(ref);
  const before = snap.exists() ? snap.data() : null;

  // 변경 전 본문을 terms_versions 에 백업 (실패해도 본 저장은 진행)
  if (before) {
    try {
      const adminUser = auth?.currentUser || null;
      await addDoc(collection(db, VERSIONS_COL), {
        termKey: type,
        termTitle: before.title || '',
        version: before.version || '',
        effective_date: before.effective_date || '',
        content: before.content || '',
        archived_at: serverTimestamp(),
        archived_by_uid: adminUser?.uid || '',
        archived_by_email: adminUser?.email || '',
        // 다음 버전 정보 (참고용)
        next_version: patch.version || '',
      });
    } catch (archiveErr) {
      console.warn('약관 이전 버전 백업 실패:', archiveErr);
    }
  }

  const merged = {
    ...(before || DEFAULT_TERMS[type] || {}),
    ...patch,
    type,
    updated_at: serverTimestamp(),
  };
  if (snap.exists()) {
    await updateDoc(ref, merged);
  } else {
    await setDoc(ref, merged);
  }
  return merged;
};

// ────── 버전 이력 조회 ──────

export const listTermVersions = async (termKey, { limit = 50 } = {}) => {
  const q = query(
    collection(db, VERSIONS_COL),
    where('termKey', '==', termKey),
    orderBy('archived_at', 'desc'),
    fsLimit(limit),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
